import type { RouteComponentProps } from 'react-router-dom';
import { Redirect } from 'react-router-dom';

import { SECTION } from 'src/constants/section';
import { routePaths } from 'src/services/routing/routePaths';
import LoginPage from 'src/components/account/LoginPage';
import PasswordResetConfirmPage from 'src/components/account/PasswordResetConfirmPage';
import PasswordResetPage from 'src/components/account/PasswordResetPage';
import CatchNullURL from 'src/components/app/CatchNullURL';
import StartPage from 'src/components/app/StartPage';
import TermsPage from 'src/components/app/TermsPage';
import ChatbotTermsPage from 'src/components/chatbot/ChatbotTermsPage';
import DashboardPage from 'src/components/dashboard/DashboardPage';
import TicketOverviewPage from 'src/components/followUp/TicketOverviewPage';
import FollowUpInspirationsPage from 'src/components/followUpInspirations/FollowUpInspirationsPage';
import FollowUpActionPage from 'src/components/myFollowUp/FollowUpActionPage/FollowUpActionPage';
import MyFollowUpPage from 'src/components/myFollowUp/MyFollowUpPage/MyFollowUpPage';
import PlanActionDetailPage from 'src/components/planAction/PlanActionDetailPage';
import PlanActionOverviewPage from 'src/components/planAction/PlanActionOverviewPage';
import PlanActionStatisticsPage from 'src/components/planAction/PlanActionStatisticsPage';
import CustomReportsPage from 'src/components/report/CustomReportsPage';
import OrgUnitReportsPage from 'src/components/report/OrgUnitReportsPage';
import SurveySelectionPage from 'src/components/survey/SurveySelectionPage';
import SurveySettingsPage from 'src/components/survey/SurveySettings';
import UserDetailPage from 'src/components/userAdmin/UserDetailPage';
import UserSearchListPage from 'src/components/userAdmin/UserListPage';
import WorkspacePage from 'src/components/workspace/WorkspacePage';

export interface AppRoute {
  component:
    | React.ComponentType<RouteComponentProps<any>>
    | React.ComponentType<any>
    | undefined;
  path: string;
  section: number;
}

/**
 *  IMPORTANT: DO NOT IMPORT APP ROUTES INSIDE REGULAR COMPONENTS
 *  This file contains various large imports from all over the app
 *  and because of that it has the potential to slow down tests or
 *  other parts of the app.
 */
const appRoutes: AppRoute[] = [
  {
    // this route is added due to an unresolvable bug concerning only the IE11 in the network of the DHL
    section: SECTION.PUBLIC,
    component: CatchNullURL,
    path: routePaths.NULL,
  },
  {
    section: SECTION.PUBLIC,
    component: () => <Redirect to={routePaths.LOGIN} />,
    path: routePaths.INDEX,
  },
  {
    section: SECTION.PUBLIC,
    component: LoginPage,
    path: routePaths.LOGIN,
  },
  {
    section: SECTION.PUBLIC,
    component: PasswordResetPage,
    path: routePaths.RESET,
  },
  {
    section: SECTION.PUBLIC,
    component: PasswordResetConfirmPage,
    path: routePaths.PASSWORD_RESET_CONFIRM,
  },
  {
    section: SECTION.PUBLIC,
    component: StartPage,
    path: routePaths.START,
  },
  {
    section: SECTION.PUBLIC,
    component: TermsPage,
    path: routePaths.TERMS_OF_USAGE,
  },
  {
    section: SECTION.PUBLIC,
    component: ChatbotTermsPage,
    path: routePaths.TERMS_OF_USAGE_CHATBOT,
  },
  {
    section: SECTION.GET_REPORT,
    component: OrgUnitReportsPage,
    path: routePaths.REPORT,
  },
  {
    section: SECTION.GET_REPORT_PRO,
    component: WorkspacePage,
    path: routePaths.WORKSPACE,
  },
  {
    section: SECTION.GET_REPORT_PRO,
    component: CustomReportsPage,
    path: routePaths.MY_REPORTS,
  },
  {
    section: SECTION.PLAN_ACTION,
    component: PlanActionOverviewPage,
    path: routePaths.PLAN_ACTION_OVERVIEW,
  },
  {
    section: SECTION.PLAN_ACTION,
    component: PlanActionStatisticsPage,
    path: routePaths.PLAN_ACTION_STATISTICS,
  },
  {
    section: SECTION.PLAN_ACTION,
    component: PlanActionDetailPage,
    path: routePaths.PLAN_ACTION_CREATE,
  },
  {
    section: SECTION.PLAN_ACTION,
    component: PlanActionDetailPage,
    path: routePaths.PLAN_ACTION_EDIT,
  },
  {
    section: SECTION.DASHBOARD,
    component: DashboardPage,
    path: routePaths.DASHBOARD_DETAIL,
  },
  {
    section: SECTION.USER_ADMIN,
    component: UserSearchListPage,
    path: routePaths.USER_ADMIN_LIST,
  },
  {
    section: SECTION.USER_ADMIN,
    component: UserDetailPage,
    path: routePaths.USER_ADMIN_DETAIL,
  },
  {
    section: SECTION.FOLLOW_UP_INSPIRATIONS,
    component: FollowUpInspirationsPage,
    path: routePaths.FOLLOW_UP_INSPIRATIONS,
  },
  {
    section: SECTION.FOLLOW_UP,
    component: TicketOverviewPage,
    path: routePaths.TICKET_OVERVIEW,
  },
  {
    section: SECTION.SURVEY,
    component: SurveySelectionPage,
    path: routePaths.SURVEY_LIST,
  },
  {
    section: SECTION.SURVEY,
    component: SurveySettingsPage,
    path: routePaths.SURVEY_SETTINGS,
  },
  {
    section: SECTION.MY_FOLLOW_UP,
    component: MyFollowUpPage,
    path: routePaths.MY_FOLLOW_UP,
  },
  {
    section: SECTION.FOLLOW_UP_ACTION,
    component: FollowUpActionPage,
    path: routePaths.FOLLOW_UP_ACTIONS,
  },
];

export default appRoutes;
